import { Component, OnDestroy, OnInit, effect, inject, input } from '@angular/core';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import { AuthorizationService } from '@myapp/modules/fwcore/services/authorization.service';
import { IAuthMenu } from '../../models';
import { RequestAccess, RequestMenu } from '../../models/messages';
import { Subject, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { HelpEntryService } from '../../../../features/prob/services';
import { ContactSupportService } from '../../services/contact-support.service';

@Component({
  selector: 'app-menu-vertical',
  templateUrl: './menu-vertical.component.html',
  styleUrls: ['./menu-vertical.component.scss'],
  standalone: true,
  imports: [NgTemplateOutlet, NgFor, NgIf, NgClass, RouterLink]
})
export class MenuVerticalComponent implements OnInit, OnDestroy {

  /** Opens Help in its own tab, resolving context first. */
  private helpEntry = inject(HelpEntryService);

  /** Opens the support form. Shared with the ? at the top of every page. */
  private contactSupport = inject(ContactSupportService);


  // ---------------- Inputs ----------------

  /** Current impersonation email — triggers menu refresh when changed. */
  public impersonationEmail = input<string | null>(null);

  // ---------------- Data properties ----------------

  menuItems!: IAuthMenu[];
  selected!: IAuthMenu;
  currExpandedItem!: IAuthMenu;
  baseExpandedItem!: IAuthMenu;

  // ---------------- State properties ----------------

  protected destroy$ = new Subject();
  protected prevImpersonationEmail?: string | null;

  // ---------------- Constructor ----------------

  constructor(
    private router: Router,
    private authService: AuthorizationService
  ) {
    // Refresh menu selection on every navigation
    router.events
      .pipe(takeUntil(this.destroy$))
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.refreshSelection(this.menuItems, event.url);
        }
      });

    // Re-fetch menu when auth state changes — handles return from Okta login,
    // token renewal, and initial authentication.
    this.authService.authenticationChanged
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAuth: boolean) => {
        if (isAuth) {
          this.fetchServerData();
        }
      });

    // Refresh menu when impersonation email changes
    effect(() => {
      const currentValue = this.impersonationEmail();
      if (currentValue !== this.prevImpersonationEmail) {
        this.prevImpersonationEmail = currentValue;
        this.refreshMenu();
      }
    });
  }

  // ---------------- ng ----------------

  ngOnInit(): void {
    this.retrieveData();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  // ---------------- Data retrieval ----------------

  /** Loads menu from cache if available, otherwise fetches from server. */
  public retrieveData(): void {
    // localStorage cache disabled until cache invalidation is implemented
    const storedMenuData = null;

    if (storedMenuData && JSON.parse(storedMenuData).length > 0) {
      this.updateMenuItems(JSON.parse(storedMenuData));
    } else {
      this.fetchServerData();
    }
  }

  /** Forces a fresh menu fetch from the server, bypassing any cache. */
  public refreshMenu(): void {
    this.fetchServerData();
  }

  /** Fetches menu items from the server — only proceeds when auth and token are ready. */
  protected fetchServerData(): void {
    const req: RequestMenu = new RequestMenu();
    req.hierarchyCode = 'main';

    this.authService.isReadyToUse().then((ready: boolean) => {
      if (!ready) return;

      const licReq = new RequestAccess();
      licReq.route = '/home';

      this.authService.hasAccess$(licReq)
        .pipe(
          map(result => result.isLicensed === true),
          catchError(() => of(false)),
          takeUntil(this.destroy$)
        )
        .subscribe(isLicensed => {
          if (!isLicensed) {
            this.updateMenuItems([]);
            return;
          }

          this.authService.menuGet$(req)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (data: IAuthMenu[]) => {
                this.updateMenuItems(data);
                // localStorage cache disabled until cache invalidation is implemented
                // localStorage.setItem('menuData', JSON.stringify(data));
              },
              error: (err) => console.error('Failed to load menu', err)
            });
        });
    });
  }

  // ---------------- Menu state ----------------

  /** Updates menu items and refreshes the current route selection. */
  protected updateMenuItems(data: IAuthMenu[]): void {
    this.carryOverExpansion(this.menuItems, data);
    this.refreshSelection(data, this.router.url);
    this.expandSelectedBranch(data);
    this.menuItems = data;
  }

  /**
   * Copies which groups were open onto the freshly fetched menu.
   *
   * Expansion is UI state, not server data. The menu is re-fetched on
   * navigation, and the new objects arrive with expanded undefined - so without
   * this, opening a group and then clicking anything inside it collapsed the
   * group again, every time.
   */
  protected carryOverExpansion(previous: IAuthMenu[] | undefined, next: IAuthMenu[]): void {

    if (!previous || !next) return;

    const wasExpanded = new Map<string, boolean>();

    const collect = (items: IAuthMenu[]): void => {
      for (const item of items) {
        if (item.id) wasExpanded.set(String(item.id), !!item.expanded);
        if (item.children && item.children.length > 0) collect(item.children);
      }
    };

    const apply = (items: IAuthMenu[]): void => {
      for (const item of items) {
        const previouslyExpanded = item.id ? wasExpanded.get(String(item.id)) : undefined;
        if (previouslyExpanded !== undefined) item.expanded = previouslyExpanded;
        if (item.children && item.children.length > 0) apply(item.children);
      }
    };

    collect(previous);
    apply(next);
  }

  /**
   * Opens the group containing the current route.
   *
   * Arriving at a page inside a collapsed group - by deep link, reload, or the
   * top-bar Help entry - would otherwise leave the navigation showing no sign of
   * where the user actually is. Relies on refreshSelection having already
   * propagated selection up to the parents.
   */
  protected expandSelectedBranch(items: IAuthMenu[]): void {

    if (!items) return;

    for (const item of items) {
      if (item.hasChildren && item.children) {
        if (item.selected) item.expanded = true;
        this.expandSelectedBranch(item.children);
      }
    }
  }

  /**
   * Marks menu items as selected based on the current URL.
   *
   * Only the MOST SPECIFIC match is selected. This used to test
   * newUrl.includes(item.url), which selected every item whose route was a
   * substring of the current one - so on /help/glossary both 'Glossary' and
   * 'User Guide' (/help) highlighted, and in the admin menu 'Model System'
   * (/admin/prob/model) highlighted whenever /admin/prob/model-files or
   * /admin/prob/model-release was open.
   *
   * Matching is on whole segments, so /admin/prob/model no longer matches
   * /admin/prob/model-release at all, and where two routes are genuinely nested
   * the longer one wins.
   */
  public refreshSelection(items: IAuthMenu[], newUrl: string): boolean {
    if (!items) return false;

    const path = this.toRoutePath(newUrl);
    const longestMatch = this.findLongestMatch(items, path);

    return this.applySelection(items, path, longestMatch);
  }

  /** Strips the query string, the fragment and any trailing slash. */
  protected toRoutePath(url: string): string {

    if (!url) return '';

    return url.split('?')[0].split('#')[0].replace(/\/+$/, '').toLowerCase();
  }

  /** Whether a menu route matches the current path on a whole-segment boundary. */
  protected isRouteMatch(itemUrl: string | undefined | null, path: string): boolean {

    if (!itemUrl || !path) return false;

    const route = this.toRoutePath(itemUrl);

    if (!route) return false;

    return path === route || path.startsWith(route + '/');
  }

  /** The length of the most specific matching route anywhere in the tree. */
  protected findLongestMatch(items: IAuthMenu[], path: string): number {

    let longest = 0;

    for (const item of items) {

      if (this.isRouteMatch(item.url, path)) {
        const length = this.toRoutePath(item.url!).length;
        if (length > longest) longest = length;
      }

      if (item.children && item.children.length > 0) {
        const fromChildren = this.findLongestMatch(item.children, path);
        if (fromChildren > longest) longest = fromChildren;
      }
    }

    return longest;
  }

  /** Selects the winning item, and any ancestor that contains it. */
  protected applySelection(items: IAuthMenu[], path: string, longestMatch: number): boolean {

    let anySelected = false;

    for (const item of items) {

      const childSelected = (item.children && item.children.length > 0)
        ? this.applySelection(item.children, path, longestMatch)
        : false;

      const selfSelected = longestMatch > 0
        && this.isRouteMatch(item.url, path)
        && this.toRoutePath(item.url!).length === longestMatch;

      item.selected = childSelected || selfSelected;

      if (item.selected) anySelected = true;
    }

    return anySelected;
  }

  /** Records the level on the item for accordion behaviour tracking. */
  public assignLevel(item: IAuthMenu, level: number): number {
    item.level = level;
    return level;
  }

  /** Returns true if the item URL is a bookmark anchor link. */
  public isBookmark(item: IAuthMenu): boolean {
    return !!(item.url && item.url.includes('#'));
  }

  /** Returns true if the URL is an internal app route rather than an external link. */
  public isItemRouteInternal(url: string): boolean {
    return !url.startsWith('http');
  }

  /**
   * The route the Contact Support menu item carries.
   *
   * It is the Contact Support SECTION in the help tree, which is real content, so a
   * ctrl-click or a middle-click leads somewhere rather than nowhere. A plain click
   * never navigates - see onSupportItemClick.
   */
  protected readonly ctSupportRoute = '/help/support';

  /**
   * True for the Contact Support item, which opens a modal instead of navigating.
   *
   * Checked BEFORE the Help test and excluded from it, because this route sits under
   * /help and would otherwise be opened in the Help tab - which is the one thing it
   * must not do.
   */
  public isItemRouteSupport(url: string | null | undefined): boolean {

    const path = (url ?? '').split('?')[0].split('#')[0];

    return path.toLowerCase() === this.ctSupportRoute;
  }

  /**
   * Opens the Contact Support form over whatever page the reader is on.
   *
   * The same service the ? at the top uses, so the two entry points cannot drift apart.
   * The anchor keeps its href, so ctrl-click and middle-click still open the section in
   * the normal way; only the plain click is taken over.
   */
  public onSupportItemClick(event: MouseEvent): void {

    event.preventDefault();

    this.contactSupport.open();
  }

  /**
   * True for a reader-facing Help route, which the menu opens in the Help tab rather
   * than routing to in place.
   *
   * Scoped to Help on purpose - this is not a general "open menu items in tabs" change.
   * Since US 33751 the menu carries one Help route, System Guide at /help; the sections
   * that used to sit beside it moved into the strip across the top of Help. It
   * deliberately does NOT cover /admin/prob/help, the Help CMS, which is an
   * administration screen and belongs in the normal flow, and it excludes the Contact
   * Support item, which opens a modal.
   *
   * Why Help is special: the reader is usually mid-task when they ask for help, and on
   * the scenario page that task holds a deal lock and guards unsaved changes. Routing
   * away would make asking for help cost them their work.
   */
  public isItemRouteHelp(url: string | null | undefined): boolean {

    return !this.isItemRouteSupport(url) && this.helpEntry.isHelpRoute(url);
  }

  /**
   * Opens a Help menu item in the Help tab.
   *
   * The anchor keeps a real href, so ctrl-click and middle-click still do what the
   * browser normally does. This only takes over the plain click.
   */
  public onHelpItemClick(event: MouseEvent, url: string | null | undefined): void {

    event.preventDefault();

    this.helpEntry.open(url);
  }

  /** Returns the CSS class string for a menu item icon. */
  public getImageClass(item: IAuthMenu): string {
    if (!item) return '';
    if (item.cssClass && item.cssClass.length > 0) {
      return 'isg-mitem-image ' + item.cssClass;
    }
    return '';
  }

  /**
   * Toggles a menu item's expanded state.
   * Implements accordion behaviour at level 2 — only one item expanded at a time.
   */
  public toggleSubmenu(ev: Event, item: IAuthMenu): void {
    if (!item) return;

    const isExpanding = !item.expanded;

    if (this.currExpandedItem && isExpanding) {
      this.currExpandedItem.currExpanded = false;
    }

    if (isExpanding && item.level === 2 && this.baseExpandedItem) {
      this.baseExpandedItem.expanded = false;
    }

    item.expanded = isExpanding;
    item.currExpanded = isExpanding;

    if (isExpanding) {
      this.currExpandedItem = item;
      if (item.level === 2) {
        this.baseExpandedItem = item;
      }
    }

    ev.preventDefault();
  }
}
